import { create } from 'zustand';

/**
 * Launcher V2 — Codex round-4 Finding #1: `WaterfallCard`'s in-flight-reorder
 * lock must survive the component unmounting when the play-flow stepper
 * navigates away from the Qualify step.
 *
 * Bug this guards: `saving` used to be plain `useState` local to
 * `WaterfallCard` (`components/play-flow/QualifyStep.tsx`). Glaze's `Tabs`/
 * `TabPanel` truly UNMOUNTS an inactive panel (not hidden-via-CSS — see
 * `rung-scope-store.ts`'s header, which hit the identical unmount behavior
 * for the rung-scope selection), and the step tabs stay freely navigable
 * (including via "Continue") while a reorder POST is still in flight. So a
 * user could start a reorder, tab away and back within that request's
 * round-trip window, and land on a FRESH `WaterfallCard` instance whose local
 * `saving` resets to `false` — silently re-enabling the drag surface (round
 * 1's fix) even though the original write had not resolved yet, and making a
 * second reorder composable against a possibly-stale head from a single
 * ordinary session.
 *
 * `handleReorder`'s async closure is not tied to an effect and is never
 * cancelled — it keeps running after the component that started it unmounts.
 * A module-scope zustand store (root CLAUDE.md "Zustand for Module-Scope
 * Client State" convention — the identical fix shape `rung-scope-store.ts`
 * already uses for this same class of problem) means that closure's
 * `setWaterfallSaving` calls land on state a REMOUNTED `WaterfallCard`
 * (or a completely different `WaterfallCard` for the same Playbook) reads
 * fresh, instead of being silently dropped by an unmounted `useState`
 * setter or reset to `false` by a brand new instance's initial state.
 *
 * Keyed by `playbookId` (not a bare boolean) so a lock never leaks across
 * different Playbooks; `PlayFlowShell` clears the whole map on a genuine
 * Play change (mirrors `clearRungScope`), not on a mere step-tab switch —
 * switching tabs does not unmount `PlayFlowShell` itself, only the
 * `TabPanel`s beneath it, which is exactly the boundary this store is
 * designed to survive.
 */
interface WaterfallSaveState {
  savingByPlaybookId: Record<string, boolean>;
}

export const initialState: WaterfallSaveState = { savingByPlaybookId: {} };

export const useWaterfallSaveStore = create<WaterfallSaveState>(() => ({ ...initialState }));

export function setWaterfallSaving(playbookId: string, saving: boolean): void {
  useWaterfallSaveStore.setState((state) => ({
    savingByPlaybookId: { ...state.savingByPlaybookId, [playbookId]: saving },
  }));
}

export function selectWaterfallSaving(playbookId: string) {
  return (state: WaterfallSaveState): boolean => state.savingByPlaybookId[playbookId] === true;
}

/** Resets the whole lock map — called by `PlayFlowShell` on a genuine Play change. */
export function clearWaterfallSaveStore(): void {
  useWaterfallSaveStore.setState({ ...initialState });
}
